import { useCallback, useEffect, useMemo } from 'react';
import { isEqual } from 'lodash-es';
import { useBoolean } from '~/Hooks';
import { useProviderFilters, FilterType } from '~/Hooks/useFilter/useProviderFilters';

type TrackingEvents = {
  onOpen?: (param?: Record<string, boolean | string | any[]>) => void;
  onApply?: (param?: Record<string, boolean | string | any[]>) => void;
  onClear?: (param?: Record<string, boolean | string | any[]>) => void;
};

type Group = {
  query?: { [key: string]: string | string[] };
  title: string;
  name: string;
  elements: JSX.Element;
  onClearMap: () => void;
  onApplyMap: () => void;
  updateSelectedMap: () => void;
  appliedSelectionIds: string[];
  selectedCount: number;
};

interface IUseGroupFilterProps {
  groups: Group[];
  trackingEvents?: TrackingEvents;
  filterType: FilterType;
}

export const useGroupFilter = ({ groups, filterType, trackingEvents }: IUseGroupFilterProps) => {
  const { appliedFilters, updateFilters } = useProviderFilters({ filterType });
  const { state: open, setTrue: onOpen, setFalse: onClose } = useBoolean(false);

  const selectedCount = useMemo(() => groups.reduce((acc, cur) => acc + cur.selectedCount, 0), [
    groups,
  ]);
  const onClear = useCallback(() => {
    if (trackingEvents?.onClear) {
      trackingEvents.onClear();
    }
    groups.forEach((group) => group.onClearMap());
    onClose();
  }, [groups]);
  const onApply = useCallback(() => {
    const selectedGroupNames = groups.reduce(
      (keys, { selectedCount, name }) => (selectedCount ? keys.concat(name) : keys),
      [] as string[],
    );
    if (trackingEvents?.onClear) {
      trackingEvents.onClear({ name: selectedGroupNames });
    }

    groups.forEach((group) => group.onApplyMap());

    onClose();
  }, [groups]);

  useEffect(() => {
    if (open) {
      if (trackingEvents?.onOpen) {
        trackingEvents.onOpen();
      }
    }
    groups.forEach((group) => group.updateSelectedMap());
  }, [open]);

  useEffect(() => {
    const newFilters = Object.assign(
      {},
      ...groups.map(({ name, appliedSelectionIds }) => ({ [name]: appliedSelectionIds })),
    );
    if (!isEqual(appliedFilters, newFilters)) {
      updateFilters(newFilters);
    }
  }, [groups]);

  return {
    open,
    groups,
    onOpen,
    onClose,
    onClear,
    onApply,
    selectedCount,
    appliedFilters,
  };
};
